import React, { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";
import html2canvas from "html2canvas";
import { HiDownload } from "react-icons/hi";
import { FaMale, FaFemale, FaTransgenderAlt } from "react-icons/fa";

export default function PatientOpCard({
  show,
  onClose,
  selectedPatient,
  hospitalName,
  logo,
}) {
  const opCardRef = useRef(null);
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (show && barcodeRef.current && selectedPatient?.patientId) {
      JsBarcode(barcodeRef.current, selectedPatient.patientId, {
        format: "CODE128",
        width: 2,
        height: 50,
        displayValue: false,
        margin: 0,
      });
    }
  }, [show, selectedPatient?.patientId]);

  if (!show) return null;

  const downloadCard = async () => {
    if (!opCardRef.current) return;

    const card = opCardRef.current;
    const closeBtn = card.querySelector('[data-role="close-btn"]');
    const saveBtn = card.querySelector('[data-role="save-btn"]');

    if (closeBtn) closeBtn.style.display = "none";
    if (saveBtn) saveBtn.style.display = "none";

    const canvas = await html2canvas(card, {
      scale: 2,
      useCORS: true,
      backgroundColor: null,
    });

    if (closeBtn) closeBtn.style.display = "";
    if (saveBtn) saveBtn.style.display = "";

    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `OPCard_${selectedPatient.patientId}.png`;
    link.click();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target.id === "op-card-overlay") onClose();
      }}
      id="op-card-overlay"
    >
      <div
        ref={opCardRef}
        className="bg-white rounded-xl shadow-xl p-0 max-w-md w-full relative border overflow-hidden font-sans mx-4 min-h-[220px]"
        style={{
          backgroundImage: `url(${logo})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "75%",
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl z-10"
          data-role="close-btn"
        >
          ✕
        </button>

        <button
          onClick={downloadCard}
          className="absolute top-4 left-2 text-gray-500 hover:text-blue-600 text-xl z-10"
          title="Download OP Card"
          data-role="save-btn"
        >
          <HiDownload />
        </button>

        <div className="p-4 relative px-6 bg-white/90">
          <div className="text-center mb-1">
            <svg width="100%" height="26" preserveAspectRatio="xMidYMid meet">
              <defs>
                <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#1D4ED8" />
                  <stop offset="50%" stopColor="#06B6D4" />
                  <stop offset="100%" stopColor="#14B8A6" />
                </linearGradient>
              </defs>
              <text
                x="50%"
                y="50%"
                dominantBaseline="middle"
                textAnchor="middle"
                fontSize="26"
                fontWeight="bold"
              >
                <tspan fill="url(#grad)">{hospitalName}</tspan>
              </text>
            </svg>
          </div>

          <svg ref={barcodeRef} className="w-72 h-28 mb-1" />

          <div className="space-y-2">
            <div className="text-lg font-bold">
              {selectedPatient?.salutation} {selectedPatient?.name}
            </div>

            <div className="flex items-center gap-2">
              {selectedPatient?.gender === "Male" ? (
                <FaMale className="text-blue-700" />
              ) : selectedPatient?.gender === "Female" ? (
                <FaFemale className="text-red-600" />
              ) : (
                <FaTransgenderAlt className="text-gray-600" />
              )}
              <span className="text-xs font-semibold">
                {selectedPatient?.gender || "Other"}
              </span>
            </div>

            <div className="font-semibold">
              OP No: {selectedPatient?.patientId}
            </div>

            <div className="text-sm text-gray-600">
              Date of Reg:{" "}
              {selectedPatient?.registrationDatetime &&
                new Date(
                  selectedPatient.registrationDatetime
                ).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
